import React from "react"
import { BLOCKS, MARKS } from "@contentful/rich-text-types"
import {
  CuricullamContainer,
  CurriculumListContainer,
  ImgCover,
  List,
  OverviewContainer,
  Title,
  TypographyDescription,
  ButtonSection
} from "./styles"

import {
  ContentfulRichTextGatsbyReference,
  renderRichText,
  RenderRichTextData,
} from "gatsby-source-contentful/rich-text"

import { StaticImage } from "gatsby-plugin-image"

interface CourseProps {
  isCertificate: Boolean
  courseContent: RenderRichTextData<ContentfulRichTextGatsbyReference>
  lessons: Array<String>
  funding: Array<String>
  assessment: Array<String>
  requirements: Array<String>
  skillsFramework: RenderRichTextData<ContentfulRichTextGatsbyReference>
  projectImplementation: Array<String>
  courseRelevanceToEmployment: Array<String>
}

const options = {
  // Render Rich Text from Contentful API
  // See https://www.gatsbyjs.com/blog/how-to-use-the-contentful-rich-text-field-with-gatsby/
  renderMark: {
    [MARKS.BOLD]: (text: any) => (
      <span className="bold">{text}</span>
    ),
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => (
      <p>{children}</p>
    ),
    [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
      return (
        <>
          <h2>Embedded Asset</h2>
          <pre>
            <code>{JSON.stringify(node, null, 2)}</code>
          </pre>
        </>
      )
    },
  },
  renderText: (text) => {
    return text
      .split("\n")
      .reduce((children, textSegment, index) => {
        return [
          ...children,
          index > 0 && <br key={index} />,
          textSegment,
        ]
      }, [])
  },
}

const ItemDescription = (props: CourseProps) => {
  const {
    isCertificate,
    courseContent,
    lessons,
    courseRelevanceToEmployment,
    funding,
    assessment,
    requirements,
    skillsFramework,
    projectImplementation,
  } = props

  const richText = () => (
    <div>
      {courseContent &&
        renderRichText(courseContent, options)}
    </div>
  )

  return (
    <div>
      <OverviewContainer>
        <Title>
          {" "}
          {isCertificate ? "Certification" : "Course"}{" "}
          Overview
        </Title>
        <TypographyDescription>
          {isCertificate
            ? "This credential helps organizations identify and develop talent with critical skills for implementing cloud initiatives. Earning AWS Certified Developer – Associate validates the ability to write and deploy cloud-based applications."
            : richText()}
        </TypographyDescription>
      </OverviewContainer>
      <CuricullamContainer>
        <Title>
          {isCertificate
            ? "Certification Objectives"
            : "Course Curriculum"}
        </Title>
        <CurriculumListContainer>
          {isCertificate ? (
            <>
              <List>
                <ImgCover>
                  <StaticImage
                    src="../../../assets/images/check.png"
                    alt=""
                  />
                </ImgCover>
                <p>
                  Demonstrate an understanding of core AWS
                  services, uses, and basic AWS architecture
                  best practices
                </p>
              </List>
              <List>
                <ImgCover>
                  <StaticImage
                    src="../../../assets/images/check.png"
                    alt=""
                  />
                </ImgCover>
                <p>
                  Demonstrate proficiency in developing,
                  deploying, and debugging cloud-based
                  applications by using AWS
                </p>
              </List>
            </>
          ) : (
            <>
              {lessons
                ? lessons.map((lesson) => {
                  return (
                    <List>
                      <ImgCover>
                        <StaticImage
                          src="../../../assets/images/check.png"
                          alt=""
                        />
                      </ImgCover>
                      <p>{lesson}</p>
                    </List>
                  )
                })
                : null}
            </>
          )}
        </CurriculumListContainer>
      </CuricullamContainer>


      <CuricullamContainer>
        {requirements ? (
          <>
            {courseRelevanceToEmployment?.length > 0 && (
            <>
              <Title>Course Relevance To Employment</Title>
              <CurriculumListContainer>
                <>
                  {courseRelevanceToEmployment.map((courseRelevanceToEmployment) => (
                    <List key={courseRelevanceToEmployment}>
                      <ImgCover>
                        <StaticImage src="../../../assets/images/check.png" alt="" />
                      </ImgCover>
                      <p>{courseRelevanceToEmployment}</p>
                    </List>
                  ))}
                </>
              </CurriculumListContainer>
            </>
          )}
            <Title>Funding</Title>
            <CurriculumListContainer>
              <>
                {funding
                  ? funding.map((funding) => {
                    return (
                      <List>
                        <ImgCover>
                          <StaticImage
                            src="../../../assets/images/check.png"
                            alt=""
                          />
                        </ImgCover>
                        <p>{funding}</p>
                      </List>
                    )
                  })
                  : null}
              </>
            </CurriculumListContainer>
            <Title>Assessment</Title>
            <CurriculumListContainer>
              <>
                {assessment
                  ? assessment.map((assessment) => {
                    return (
                      <List>
                        <ImgCover>
                          <StaticImage
                            src="../../../assets/images/check.png"
                            alt=""
                          />
                        </ImgCover>
                        <p>{assessment}</p>
                      </List>
                    )
                  })
                  : null}
              </>
            </CurriculumListContainer>
            <Title>Entry Requirements</Title>
            <CurriculumListContainer>
              <>
                {requirements
                  ? requirements.map((req) => {
                    return (
                      <List>
                        <ImgCover>
                          <StaticImage
                            src="../../../assets/images/check.png"
                            alt=""
                          />
                        </ImgCover>
                        <p>{req}</p>
                      </List>
                    )
                  })
                  : null}
              </>
            </CurriculumListContainer>
            {projectImplementation?.length > 0 && (
            <>
              <Title>Project Implementation</Title>
              <CurriculumListContainer>
                <>
                  {projectImplementation.map((projectImplementation) => (
                    <List key={projectImplementation}>
                      <ImgCover>
                        <StaticImage src="../../../assets/images/check.png" alt="" />
                      </ImgCover>
                      <p>{projectImplementation}</p>
                    </List>
                  ))}
                </>
              </CurriculumListContainer>
            </>
          )}
          </>
        ) : null}
      </CuricullamContainer>
      <OverviewContainer>
        <TypographyDescription>
          {skillsFramework ? (
            <>
              <Title>Skillsframework</Title>
              <p>{skillsFramework}</p>
            </>
          ) : null}
        </TypographyDescription>
      </OverviewContainer>
    </div>
  )
}

export default ItemDescription